/**
 * A/B split between the classic customizer and the AI Mockup Studio.
 *
 * Scope: only the logo flow. Every CTA that leads to the logo editor points
 * at the classic page's path, and the text products (phone, WhatsApp,
 * Instagram, custom text) use a different customizer on their own
 * templates, so matching on that path keeps them out of the test.
 *
 * Assignment happens when a visitor is EXPOSED to a covered CTA, not when
 * they click it. Assigning on click would make click-through unmeasurable,
 * since everyone assigned would have clicked by definition.
 *
 * Stickiness, in priority order. A variant is only rolled when all four are
 * empty, so a visitor never flips between editors:
 *   1. URL parameter   works even with storage blocked
 *   2. Cookie
 *   3. localStorage
 *   4. Shopify cart attribute   survives Safari ITP clearing the two above
 *
 * Visitors who land directly on an editor (organic, bookmark, shared link)
 * keep the editor they landed on but are flagged as not randomized, and the
 * reporting excludes them: the comparison is only valid over randomized
 * traffic.
 */
(function () {
  'use strict';

  var CFG = window.AB_EDITOR_CONFIG || {};
  if (!CFG.enabled) return;

  /* Never assign inside the theme editor: it would burn assignments and
     inflate the denominator with the store owner's own previews. */
  if (window.Shopify && window.Shopify.designMode) return;
  if (/bot|crawl|spider|slurp|headless|lighthouse|pagespeed|preview/i.test(navigator.userAgent)) return;

  /* Raising ROUND restarts the test: every visitor is randomized again and
     the worker's AB_ROUND has to match so /stats counts the new round. */
  var ROUND = 2;
  var COOKIE = 'md_ab_editor_r' + ROUND;
  var LS_KEY = 'md_ab_editor_r' + ROUND;
  var PARAM = 'abv';
  var ATTR = 'AB Editor';
  var ROUND_ATTR = 'AB Round';
  var VARIANTS = { a: CFG.urlA, b: CFG.urlB };
  var NAMES = { a: 'classic', b: 'ai-studio' };

  var state = { variant: null, randomized: false, persisted: false };

  /* ── path helpers ── */
  function pathOf(url) {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/+$/, '').toLowerCase();
    } catch (e) { return ''; }
  }
  var PATH_A = pathOf(CFG.urlA);
  var PATH_B = pathOf(CFG.urlB);

  function variantForPath(p) {
    if (PATH_A && p === PATH_A) return 'a';
    if (PATH_B && p === PATH_B) return 'b';
    return null;
  }

  /* ── storage layers ── */
  function fromParam() {
    var m = new RegExp('[?&]' + PARAM + '=([ab])(&|$)').exec(window.location.search);
    return m ? m[1] : null;
  }

  function fromCookie() {
    var m = new RegExp('(?:^|; )' + COOKIE + '=([ab])(?:;|$)').exec(document.cookie);
    return m ? m[1] : null;
  }

  function writeCookie(v) {
    /* Safari caps JS-set cookies at 7 days regardless of what we ask for;
       the cart attribute is what carries the assignment past that. */
    try {
      document.cookie = COOKIE + '=' + v + '; path=/; max-age=31536000; SameSite=Lax';
    } catch (e) {}
  }

  function fromLS() {
    try {
      var v = window.localStorage.getItem(LS_KEY);
      return (v === 'a' || v === 'b') ? v : null;
    } catch (e) { return null; }
  }

  function writeLS(v) {
    try { window.localStorage.setItem(LS_KEY, v); } catch (e) {}
  }

  function deviceType() {
    try { return window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop'; } catch (e) { return null; }
  }

  /* ── tracking ── */
  function track(event, extra) {
    if (!CFG.trackUrl || !state.variant) return;
    var body = {
      event: event,
      variant: NAMES[state.variant],
      randomized: state.randomized,
      round: ROUND,
      device: deviceType(),
      path: window.location.pathname
    };
    if (extra) for (var k in extra) body[k] = extra[k];
    try {
      /* text/plain is CORS-safelisted, so the beacon goes out without a
         preflight the browser would refuse to send for sendBeacon. */
      var blob = new Blob([JSON.stringify(body)], { type: 'text/plain' });
      if (navigator.sendBeacon && navigator.sendBeacon(CFG.trackUrl + '/track', blob)) return;
    } catch (e) {}
    /* keepalive so the request survives the navigation it often precedes */
    try {
      fetch(CFG.trackUrl + '/track', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(body),
        keepalive: true
      }).catch(function () {});
    } catch (e) {}
  }

  /* ── persistence ──
     The cart attribute is written once per browser session: rewriting it on
     every page view cost a request per page and overwrote "AB Randomized". */
  var SESSION_KEY = 'md_ab_cart_synced_r' + ROUND;
  function cartSynced() { try { return window.sessionStorage.getItem(SESSION_KEY) === state.variant; } catch (e) { return false; } }
  function markCartSynced() { try { window.sessionStorage.setItem(SESSION_KEY, state.variant); } catch (e) {} }
  function readRandomized() { try { return window.localStorage.getItem(LS_KEY + '_r') !== 'no'; } catch (e) { return true; } }
  function writeRandomized(r) { try { window.localStorage.setItem(LS_KEY + '_r', r ? 'yes' : 'no'); } catch (e) {} }

  function persist() {
    if (!state.variant || state.persisted) return;
    state.persisted = true;
    writeRandomized(state.randomized);
    if (cartSynced()) { writeCookie(state.variant); writeLS(state.variant); return; }
    markCartSynced();
    writeCookie(state.variant);
    writeLS(state.variant);
    /* The cart attribute rides along to the order, which is how revenue is
       attributed per variant without any third-party analytics. */
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: (function () {
            var a = {};
            a[ATTR] = NAMES[state.variant];
            a['AB Randomized'] = state.randomized ? 'yes' : 'no';
            a[ROUND_ATTR] = String(ROUND);
            return a;
          })()
        })
      }).catch(function () {});
    } catch (e) {}
  }

  function setVariant(v, randomized) {
    if (state.variant) return;
    state.variant = v;
    state.randomized = !!randomized;
    persist();
  }

  function roll() {
    var pct = typeof CFG.splitB === 'number' ? CFG.splitB : 50;
    return (Math.random() * 100 < pct) ? 'b' : 'a';
  }

  /* ── link rewriting ── */
  function coveredLinks() {
    var out = [];
    var links = document.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      var href = a.getAttribute('href') || '';
      /* Internal links only: skip mailto/tel/external hosts */
      if (/^(https?:)?\/\//.test(href) && href.indexOf(window.location.host) === -1) continue;
      if (/^(mailto:|tel:|#)/.test(href)) continue;
      if (variantForPath(pathOf(href))) out.push(a);
    }
    return out;
  }

  function rewrite(a) {
    if (!state.variant) return;
    var dest = VARIANTS[state.variant];
    if (!dest) return;
    var sep = dest.indexOf('?') === -1 ? '?' : '&';
    a.setAttribute('href', dest + sep + PARAM + '=' + state.variant);
    a.setAttribute('data-ab-editor', NAMES[state.variant]);
  }

  function rewriteAll() {
    var links = coveredLinks();
    if (!links.length) return 0;
    for (var i = 0; i < links.length; i++) rewrite(links[i]);
    return links.length;
  }

  /* ── cart attribute fallback ──
     Only consulted when the faster layers are empty, so the common path
     costs no extra request. */
  function restoreFromCart(done) {
    try {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          /* A cart from an earlier round does not carry over: that visitor
             is randomized again like everyone else. */
          var sameRound = cart && cart.attributes && cart.attributes[ROUND_ATTR] === String(ROUND);
          var name = sameRound ? cart.attributes[ATTR] : null;
          var v = name === NAMES.b ? 'b' : (name === NAMES.a ? 'a' : null);
          if (v) {
            state.variant = v;
            state.randomized = cart.attributes['AB Randomized'] !== 'no';
            state.persisted = true;
            writeCookie(v);
            writeLS(v);
          }
          done();
        })
        .catch(done);
    } catch (e) { done(); }
  }

  /* ── cart add detection ──
     Both editors post to /cart/add.js, so wrapping fetch catches the
     add-to-cart event from either without touching their code. */
  function watchCartAdds() {
    if (!window.fetch || window.__abCartWatch) return;
    window.__abCartWatch = true;
    var orig = window.fetch;
    window.fetch = function (input, init) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
      var promise = orig.apply(this, arguments);
      if (url.indexOf('/cart/add') !== -1) {
        promise.then(function (res) {
          if (res && res.ok) track('add_to_cart');
        }).catch(function () {});
      }
      return promise;
    };
  }

  /* ── boot ── */
  function start() {
    var here = variantForPath(pathOf(window.location.pathname));
    var known = fromParam() || fromCookie() || fromLS();

    if (here) {
      /* Never bounce a visitor away from the editor they asked for. But if
         they already belong to the other arm (a shared link, say), show the
         page without overwriting their assignment: flipping it would make
         every later visit use the other editor. */
      if (known && known !== here) {
        state.variant = known;
        state.randomized = true;
        state.persisted = true;
      } else {
        setVariant(here, known ? readRandomized() : false);
      }
      watchCartAdds();
      track('editor_view', here === state.variant ? null : { landed: NAMES[here] });
      return;
    }

    if (known) {
      setVariant(known, readRandomized());
      finish();
      return;
    }

    /* Nothing stored: only roll if this page actually shows a covered CTA,
       so page views with no exposure never enter the experiment. */
    if (!coveredLinks().length) return;

    restoreFromCart(function () {
      if (!state.variant) {
        setVariant(roll(), true);
        track('assign');
      }
      finish();
    });
  }

  function finish() {
    var n = rewriteAll();
    if (!n) return;
    /* Add-to-cart is only counted on the editor pages themselves (see
       start); quick-adds elsewhere are not an editor conversion. */
    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[data-ab-editor]') : null;
      if (a) track('cta_click');
    }, true);
    /* Sections render late (drawers, popups, section rendering), so keep
       rewriting links that appear after first paint. */
    if (window.MutationObserver) {
      var mo = new MutationObserver(function () { rewriteAll(); });
      mo.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
